import type { Filter, Grid } from './types';

export const isFilterValueEmpty = (value: Filter['value']): boolean => {
    if (Array.isArray(value)) {
        return value.length === 0;
    }

    return value === null || value === '';
};

export const normalizeFilterValue = (value: Filter['value']): string => {
    if (Array.isArray(value)) {
        return JSON.stringify([...value].sort());
    }

    return JSON.stringify(value);
};

export const getActiveFilters = (filters: Filter[] | undefined): Filter[] => {
    return (filters ?? []).filter((filter) => !isFilterValueEmpty(filter.value));
};

/**
 * Clone filters so applying a grid never aliases the grid definition objects —
 * DataGridFilters mutates filter rows in place via v-model, and a shared reference
 * would silently rewrite the grid (tab) definition itself.
 */
export const cloneFilters = (filters: Filter[] | undefined): Filter[] => {
    return (filters ?? []).map((filter) => ({
        ...filter,
        value: Array.isArray(filter.value) ? [...filter.value] : filter.value,
    }));
};

const filtersInclude = (haystack: Filter[], needle: Filter): boolean => {
    return haystack.some(
        (filter) =>
            filter.field === needle.field &&
            filter.operator === needle.operator &&
            normalizeFilterValue(filter.value) === normalizeFilterValue(needle.value),
    );
};

/**
 * A grid matches when every one of its filters is present among the active filters
 * (subset semantics) — adding extra filters on top of a tab keeps the tab active.
 */
export const gridMatchesFilters = (gridFilters: Filter[] | undefined, currentFilters: Filter[]): boolean => {
    const activeGridFilters = getActiveFilters(gridFilters);

    if (activeGridFilters.length === 0) {
        return false;
    }

    return activeGridFilters.every((gridFilter) => filtersInclude(currentFilters, gridFilter));
};

/**
 * Find the grid matching the current filters. With no active filters the grid with
 * empty filters ("All") wins; otherwise the most specific matching grid (most filters).
 */
export const findMatchingGrid = (grids: Grid[], filters: Filter[]): number | null => {
    const activeFilters = getActiveFilters(filters);

    if (activeFilters.length === 0) {
        const emptyIndex = grids.findIndex((grid) => getActiveFilters(grid.filters).length === 0);

        return emptyIndex === -1 ? null : emptyIndex;
    }

    let bestIndex: number | null = null;
    let bestCount = 0;

    grids.forEach((grid, index) => {
        const activeGridFilters = getActiveFilters(grid.filters);

        if (activeGridFilters.length <= bestCount || !gridMatchesFilters(grid.filters, activeFilters)) {
            return;
        }

        bestIndex = index;
        bestCount = activeGridFilters.length;
    });

    return bestIndex;
};
